import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Play } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { DrillCard } from '@/components/training/DrillCard';
import { loadTrainingDrill, type TrainingDrill } from '@/lib/training-program/drill-loader';
import type { ProgramDrill } from '@/types/training-program';

/**
 * LibraryDrillDetailPage - Drill detail page for the Drill Library context
 * 
 * UX-AUDIT-V3-100: This page loads drills directly from the drill loader,
 * NOT requiring them to be in the user's active training plan.
 * This allows users to browse and execute ANY drill from the library.
 */
export default function LibraryDrillDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState<TrainingDrill | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!slug) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const loadedDrill = await loadTrainingDrill(slug);
        if (!cancelled) {
          setDrill(loadedDrill);
        }
      } catch (error) {
        console.error('Failed to load drill:', error);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [slug]);

  const handleBack = () => {
    navigate('/drills');
  };

  const handleStart = (drillId: string) => {
    // Pass library context so we can return here after execution
    navigate(`/drills/${drillId}/start`, { state: { from: 'library' } });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!drill) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6">
        <Card className="w-full max-w-md text-center">
          <CardContent className="p-8 space-y-3">
            <h2 className="text-xl text-[#0D382A] font-semibold">Drill Not Found</h2>
            <p className="text-sm text-gray-600">This drill could not be loaded.</p>
            <Button onClick={handleBack} className="w-full">
              Back to Drill Library
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Convert TrainingDrill to ProgramDrill format for DrillCard component
  const programDrill: ProgramDrill = {
    drill_id: drill.drill_id,
    drill_name: drill.drill_name,
    focus_area: drill.focus_area,
    program_connection_text: drill.program_connection_text,
    swing_thought: drill.swing_thought,
    how_to_perform: drill.steps || [],
    assigned_reps: drill.assigned_reps,
    what_to_look_for: drill.what_to_look_for,
    video_url: drill.video_url,
    common_mistakes: drill.common_mistakes,
    when_to_stop: null,
    mobility_modifier: null,
    is_required: false,
    why_this_helps: drill.program_connection_text,
  };

  // UX-AUDIT-V3-203: pb-40 to ensure bottom nav is accessible below sticky CTA
  return (
    <div className="min-h-screen pb-40 bg-white">
      <header className="bg-gradient-to-r from-[#0D382A] to-[#196644] text-white">
        <div className="max-w-7xl mx-auto px-6 py-6 flex items-center gap-3">
          <button
            onClick={handleBack}
            className="h-9 w-9 rounded-full border border-white/30 flex items-center justify-center hover:bg-white/10"
            aria-label="Go back"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <p className="text-sm text-white/70">Drill Library</p>
            <h1 className="text-2xl font-semibold">{drill.drill_name}</h1>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {/* Library context card - different from Training Program context */}
        <Card className="border border-[#196644]/20 bg-[#196644]/5">
          <CardContent className="space-y-2 p-4">
            <p className="text-sm text-[#196644] uppercase tracking-wide">About This Drill</p>
            <p className="text-sm text-gray-600">
              {drill.focus_area}
            </p>
            {drill.category && (
              <p className="text-sm text-gray-500">
                Category: <span className="capitalize">{drill.category.replace('_', ' ')}</span>
              </p>
            )}
          </CardContent>
        </Card>

        <DrillCard drill={programDrill} variant="expanded" showActions onStart={handleStart} />
      </main>

      {/* UX-AUDIT-V3-203: Position sticky CTA above bottom nav */}
      <div className="sticky bottom-16 left-0 right-0 z-40 bg-white border-t border-gray-200 p-4">
        <Button onClick={() => handleStart(drill.drill_id)} className="w-full gap-2">
          <Play className="w-5 h-5" />
          Start Drill
        </Button>
      </div>
    </div>
  );
}
